import { Grid } from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { User } from 'oidc-client';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuthContext } from 'src/auth/auth-context';
import { manageLogin } from 'src/command-menu/permissions/login-manager';
import { ApprovalRejectReason, IBRMMessage, IBRMRule } from 'src/common/api/brm/brm-utils';
import BaseButton from 'src/common/components/base-button';
import InputCancelAdornment from 'src/common/components/input-cancel-adornment/input-cancel-adornment';
import { MessageModal } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import XLRLogger from 'src/common/helpers/logger';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { AppState, store } from 'src/common/redux/configure-store';
import { CssTextField } from 'src/css-text-field/css-text-field';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import { popupAsyncMessage } from 'src/messages/business-logic/popup-message';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { getApproval } from './get-approval';
import { styles } from './supervisor-approval-styles';

type Props = ISupervisorApprovalState;
const SWITCH_TENDER = 'SWITCH_TENDER';

export const showSupervisorApprovalDialog = async (
  props: Props,
  onDataScanned?: ScannedDataHandler,
  modalId?: string,
): Promise<void> => {
  const { depth, rule, onAccept, onCancel } = props;
  const { separateApprovalRequest } = getCurrentXLRConfig();
  // Switch tender can only be bypassed once without error, forcing a manager to do this for now so we
  // don't lock the POS
  // TODO: Remove after https://jira.ncr.com/browse/XLR-10811 resolves
  if (separateApprovalRequest && depth === 0 && rule && rule.ruleId !== SWITCH_TENDER) {
    try {
      await getApproval(store.getState().transaction.transactionId, rule);
      onAccept('', '');
    } catch (error) {
      if (error?.code === 'UnableToValidateCredentials') {
        // This error means one of two things, either the token
        // expired or we have a invalid configuration
        const { EDGE_IAM } = getMetaData().xlrConfig;
        if (EDGE_IAM) {
          // Invalid configs are really an Edge IAM thing,
          // do a popup and move on
          await popupAsyncMessage({
            message: 'ContactAnAdministrator',
            type: EmeraldDialogTypes.Error,
            subMessage: 'UnableToValidateCredentials',
          });
        } else {
          // In this case, the token expired and the manager
          // needs to enter their credentials again
          // TODO: Refresh cloud tokens
          onAccept('', '');
        }
      } else {
        await popupAsyncMessage({
          message: 'GeneralErrorIntervention',
          type: EmeraldDialogTypes.Error,
          subMessage: 'ContactAnAdministrator',
        });
      }
      onCancel();
    }
  } else {
    pushEmeraldModal(<SupervisorApprovalModal {...props} />, onDataScanned, modalId);
  }
};

const SupervisorApprovalModal: React.FC<Props> = ({
  brmMessage,
  errorMessage,
  onAccept,
  onCancel,
  rule,
  depth,
}: Props): JSX.Element => {
  const { EDGE_IAM } = getMetaData().xlrConfig;

  const { signinPopup } = useAuthContext();

  const classes = styles();

  const [userId, setUserId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [modalErrorMessage, setMessage] = useState<string>(errorMessage || '');
  const [changeInputUnderlineColor, setchangeInputUnderlineColor] = useState<boolean>(false);
  const transactionId = useSelector((state: AppState) => state.transaction.transactionId);
  const [errors, setErrors] = useState<ILoginUserErrors>({
    supervisorId: '',
    supervisorPassword: '',
  });

  const { t } = useTranslation();

  const resetState = (): void => {
    setUserId('');
    setPassword('');
    setErrors({ supervisorId: '', supervisorPassword: '' });
  };

  const onClose = (): void => {
    resetState();
    onCancel();
  };

  const { separateApprovalRequest } = getCurrentXLRConfig();

  const onApprove = async (): Promise<void> => {
    if (userId && password) {
      resetState();
      if (separateApprovalRequest && rule) {
        try {
          const userCreds: ILoginUser = { userName: userId, password: password };
          const token = await manageLogin(userCreds);
          const creds = {
            sessionId: token.sessionId,
          };
          await getApproval(transactionId, rule, creds);
          onAccept('', '');
        } catch (error) {
          setchangeInputUnderlineColor(true);
          if (error.code === 'Unauthorized') {
            setMessage('IncorrectUserPassword');
          } else {
            setMessage('InsufficentPrivileges');
          }
        }
      } else {
        onAccept(userId, password);
      }
    } else {
      setErrors({
        supervisorId: !userId ? 'User ID is required.' : '',
        supervisorPassword: !password ? 'Pin Code is required.' : '',
      });
    }
  };
  const edgeApprove = async (user: User) => {
    if (rule) {
      const creds = { sessionId: user.access_token };
      await getApproval(transactionId, rule, creds).catch(error => {
        if (error.code === 'Unauthorized') {
          setMessage('IncorrectUserPassword');
        } else {
          setMessage('InsufficentPrivileges');
        }
        // bubble up error, otherwise the iam pop up logic will always try to accept.
        throw error;
      });
    }
  };

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = event.target.id;
    const value = event.target.value;
    switch (id) {
      case 'user-id':
        setUserId(value);
        setErrors({ ...errors, supervisorId: '' });
        break;
      case 'password':
        setPassword(value);
        setErrors({ ...errors, supervisorPassword: '' });
        break;
    }
  };

  const handleIamLogin = async () => {
    const user = await signinPopup();
    await edgeApprove(user)
      .then(() => {
        onAccept('', '');
      })
      .catch(() => {
        XLRLogger.error({ message: 'error validating token' });
      });
  };

  const rejectReason = rule?.rejectedReason;

  useEffect(() => {
    if ((depth ?? 0) > 1 && rejectReason === ApprovalRejectReason.NotAuthorized) {
      setMessage('InsufficentPrivileges');
    }
  }, [rejectReason, depth]);

  const iamProps = {
    message: brmMessage?.caption || t('SupervisorApprovalCaption'),
    subMessage: brmMessage?.message || t('SupervisorApprovalText'),
    dialogContentAboveSubMessage: true,
    onAccept: handleIamLogin,
    onCancel: onClose,
    separateButton: true,
    acceptButtonLabel: modalErrorMessage ? 'Retry' : 'GetApproval',
  };

  const marketplaceProps: IMessageModal = {
    size: MessageModalSizeEnum.FullScreen,
    message: brmMessage?.caption || t('SupervisorApprovalCaption'),
    subMessage: brmMessage?.message || t('SupervisorApprovalText'),
    hideButtons: true,
    type: EmeraldDialogTypes.None,
    fullScreen: true,
  };
  const modalProps = EDGE_IAM ? iamProps : marketplaceProps;
  return (
    <MessageModal
      dialogTestIdPrefix='supervisor-approval-dialog'
      dialogContentComponent={
        <>
          {modalErrorMessage && (
            <Grid container justifyContent='center'>
              <Grid
                item
                container
                justifyContent='flex-start'
                alignItems='center'
                className={EDGE_IAM ? classes.edgeErrorContainer : classes.errorContainer}
              >
                <ReportProblemOutlinedIcon classes={{ root: `${classes.erroricon}` }} />
                <span className={classes.errorMessage} data-testid='FormErrorLabel'>
                  {t(modalErrorMessage)}
                </span>
              </Grid>
            </Grid>
          )}
          {EDGE_IAM ? (
            <></>
          ) : (
            <>
              {' '}
              <Grid item className={classes.inputtextItem}>
                <CssTextField
                  className={
                    changeInputUnderlineColor
                      ? `${classes.inputTextFieldError}`
                      : `${classes.inputTextField}`
                  }
                  variant='standard'
                  inputProps={{
                    'data-testid': 'user-id',
                    className: 'global-keyboard',
                    'data-custombuttons': 'keyboardWithNextButton',
                  }}
                  id='user-id'
                  label='User ID'
                  name='User ID'
                  autoComplete='off'
                  autoFocus={true}
                  error={!!errors.supervisorId}
                  helperText={errors.supervisorId}
                  InputProps={{
                    endAdornment: (
                      <InputCancelAdornment
                        name='user-id'
                        state={userId}
                        resetState={(): void => setUserId('')}
                      />
                    ),
                  }}
                  value={userId}
                  onChange={onTextChange}
                />
              </Grid>
              <Grid item className={classes.inputtextItem}>
                <CssTextField
                  className={
                    changeInputUnderlineColor
                      ? `${classes.inputTextFieldError}`
                      : `${classes.inputTextField}`
                  }
                  variant='standard'
                  inputProps={{
                    'data-testid': 'password',
                    className: 'global-keyboard',
                    'data-custombuttons': 'keyboardWithNextButton',
                  }}
                  id='password'
                  label='Pin Code'
                  name='Pin Code'
                  autoComplete='off'
                  error={!!errors.supervisorPassword}
                  helperText={errors.supervisorPassword}
                  type='password'
                  InputProps={{
                    endAdornment: (
                      <InputCancelAdornment
                        name='password'
                        state={password}
                        resetState={(): void => setPassword('')}
                      />
                    ),
                  }}
                  value={password}
                  onChange={onTextChange}
                />
              </Grid>
              <Grid container justifyContent='center'>
                <Grid item className={classes.buttonContainer}>
                  <BaseButton
                    buttonType='white'
                    className={classes.closeButton}
                    onClick={onClose}
                    data-testid='cancel-button-supervisor-approval'
                  >
                    {t('Cancel')}
                  </BaseButton>
                </Grid>
                <Grid item className={classes.buttonContainer}>
                  <BaseButton
                    className={classes.approveButton}
                    onClick={onApprove}
                    data-testid='approve-button-supervisor-approval'
                    disabled={userId && password ? false : true}
                  >
                    {t('Approve')}
                  </BaseButton>
                </Grid>
              </Grid>
            </>
          )}
        </>
      }
      {...modalProps}
    />
  );
};

interface ILoginUserErrors {
  supervisorId: string;
  supervisorPassword: string;
}

export interface ISupervisorApprovalState {
  brmMessage?: IBRMMessage;
  errorMessage?: string;
  onAccept: (userId: string, password: string) => void;
  onCancel: () => void;
  rule?: IBRMRule;
  depth: number;
}

export default SupervisorApprovalModal;
